/**
 * El reparto de respuestas de la clase.
 *
 * **Cada barra lleva su número además de su longitud** (principio IV): una barra comunica por
 * tamaño, que es forma, pero un número exacto es lo que la profesora dice en voz alta. Y la correcta
 * se marca con símbolo y palabra, no con un color distinto.
 *
 * Solo se pinta en el proyector: el reparto no viaja al alumno.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ANSWER_TOKENS, type Histogram } from '@clica/shared';
import { FichaOpcion } from './ficha-opcion.js';

export function RepartoRespuestas({ histogram }: { histogram: Histogram }): React.JSX.Element {
  const { t } = useTranslation();
  const mayor = Math.max(1, ...histogram.bars.map((bar) => bar.count));

  /*
   * Las barras nacen a cero y crecen en el primer repintado.
   *
   * Sin esto la transición estaba declarada y no corría nunca: el elemento se montaba ya con su
   * anchura final, y una transición solo interpola entre un valor y otro. El histograma aparecía
   * hecho, que es justo el momento en que la clase mira la pared para ver quién ha elegido qué.
   */
  const [montado, setMontado] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setMontado(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <section aria-labelledby="reparto-titulo" className="mt-8">
      <h2 id="reparto-titulo" className="text-text-dim text-sm tracking-widest uppercase">
        {t('game.histogram.title')}
      </h2>

      {/* Las frases que la profesora usa en voz alta. Son un dato del servidor, no una deducción. */}
      {histogram.answered === 0 ? (
        <p className="mt-3 text-2xl font-semibold">{t('game.histogram.nobodyAnswered')}</p>
      ) : histogram.nobodyCorrect ? (
        <p className="text-ko mt-3 text-2xl font-semibold">{t('game.histogram.nobodyCorrect')}</p>
      ) : histogram.everybodyCorrect ? (
        <p className="text-ok mt-3 text-2xl font-semibold">
          {t('game.histogram.everybodyCorrect')}
        </p>
      ) : null}

      <ul className="mt-4 flex flex-col gap-3">
        {histogram.bars.map((bar) => {
          const token = ANSWER_TOKENS[bar.position];
          const ancho = (bar.count / mayor) * 100;

          return (
            // Envuelve: a 320 px el ancho fijo del recuento más la marca de correcta se salían
            // de la pantalla.
            <li key={bar.optionId} className="flex flex-wrap items-center gap-3">
              <FichaOpcion position={bar.position} size={40} />

              {/* El canal, con su filete: contra el papel la barra amarilla da 1,72:1 y sin
                  borde no se distingue del fondo del canal. */}
              <span className="bg-surface border-line relative h-10 flex-1 overflow-hidden rounded-lg border-2">
                <span
                  aria-hidden="true"
                  /*
                   * **La barra crece.** Antes montaba ya con su anchura final, así que la
                   * transición declarada no llegaba a correr nunca: el histograma aparecía hecho.
                   * `--dur-scene` es lo que tarda un cambio de escena, y esto es uno.
                   */
                  className="ease-hard block h-full transition-[width] duration-(--dur-scene) motion-reduce:transition-none"
                  style={{
                    width: montado ? `${String(ancho)}%` : '0%',
                    backgroundColor: token?.color,
                  }}
                />
              </span>

              {/* El número, siempre: es lo que se dice en voz alta y lo que lee un lector. */}
              <span className="min-w-24 shrink-0 text-2xl tabular-nums">
                {t('game.histogram.count', { count: bar.count })}
              </span>

              {bar.correct ? (
                <span className="text-ok flex shrink-0 items-center gap-1 text-xl font-bold">
                  <span aria-hidden="true">{CORRECT_MARK}</span>
                  {t('game.histogram.correct')}
                </span>
              ) : (
                <span className="w-28 shrink-0" />
              )}
            </li>
          );
        })}
      </ul>

      {histogram.noAnswer > 0 ? (
        <p className="text-text-dim mt-3 text-lg">
          {t('game.histogram.noAnswer', { count: histogram.noAnswer })}
        </p>
      ) : null}
    </section>
  );
}

/** La marca de la correcta. Decorativa: al lado va la palabra que dice lo mismo. */
const CORRECT_MARK = '✓';
